// src/utils/chatLanguage.js
//
// Whether an Ask Previu message needs translating, and into what the reply
// goes. Builds on textLang.js (the lang attribute from the script a message
// is written in) and adds the Latin-script case:
//
//   Malayalam script  → 'ml'
//   Devanagari        → 'hi'
//   Latin text reading as Manglish / Hinglish → 'ml' / 'hi'
//   anything else     → null: English, and no translate call is made at all
//
// The word lists and the scoring are copied from the backend's
// LanguageDetector.java (PreviuAgenticPlatform) so the browser and server
// agree on what counts as English. They hold only words that are not also
// English words ("do", "me", "main", "to", "pet" are Hinglish too); a Latin
// message is non-English when at least two words, or at least a third of its
// words, are on a list.

import textLang from './textLang';

// Manglish is answered in Malayalam script for now. One switch: set to
// 'ml-latn' (or whatever the backend comes to accept) to reply in Manglish.
export const MANGLISH_REPLY_LANG = 'ml';
export const HINGLISH_REPLY_LANG = 'hi';

const MANGLISH = new Set([
  'und', 'undu', 'undo', 'illa', 'alla', 'alle', 'ille', 'ente', 'enikku', 'ningal', 'ningalude', 'njan',
  'njangal', 'pani', 'vedana', 'nenju', 'nenjuvedana', 'chuma', 'thonda', 'gulika', 'marunnu', 'divasam',
  'divasamayi', 'divasavum', 'thavana', 'neram', 'kazhinju', 'kazhikkano', 'kazhikkanam', 'kazhikkunnund',
  'kazhikkunnu', 'raavile', 'ravile', 'raathri', 'rathri', 'raavileyum', 'raathriyum', 'vaikunneram', 'ippo',
  'ippol', 'innu', 'innale', 'nale', 'naale', 'muthal', 'aanu', 'aano', 'anu', 'ano', 'aayirunnu', 'ayirunnu',
  'vannathu', 'ithu', 'athu', 'enthu', 'entha', 'engane', 'eppol', 'evide', 'kooduthal', 'kuravu', 'verum',
  'vayattil', 'vayaru', 'varano', 'varamo', 'varunnu', 'varum', 'manikku', 'cheyyano', 'cheyyanam', 'venam',
  'venda', 'shariyaayi', 'shwasam', 'kittunnilla', 'kittiyo', 'kitti', 'thala', 'thalavedana', 'karangi',
  'veenu', 'chardi', 'kayarumbol', 'edukkunnathu', 'edukkano', 'parayamo', 'sheriyano', 'kuzhappamundo',
]);

const HINGLISH = new Set([
  'hai', 'hain', 'hoon', 'hun', 'nahi', 'nahin', 'nahee', 'kya', 'kyun', 'kyon', 'mujhe', 'mera', 'meri',
  'mere', 'kal', 'aaj', 'abhi', 'se', 'ke', 'ki', 'ka', 'ko', 'aur', 'bhi', 'raha', 'rahi', 'rahe', 'wali',
  'wala', 'goli', 'dawai', 'dawa', 'dard', 'bukhar', 'khansi', 'gala', 'kharab', 'khane', 'khana', 'baad',
  'pehle', 'subah', 'shaam', 'raat', 'roz', 'rozana', 'leni', 'lena', 'aaya', 'aayi', 'gaya', 'gayi', 'thi',
  'tha', 'baje', 'sakta', 'sakti', 'saans', 'theek', 'thik', 'behosh', 'ulti', 'dast', 'sahab', 'waqt',
  'seedhi', 'chadhte', 'hota', 'hoti', 'zyada', 'jyada', 'liye', 'aana', 'khali', 'sab', 'kab', 'kaise',
  'kitna', 'kitni', 'chahiye', 'batao', 'bataiye', 'dijiye', 'karna', 'karu', 'karoon', 'ghante', 'hafte',
  'mahine', 'chakkar', 'ghabrahat', 'kamzori', 'tabiyat',
]);

// Malayalam verb endings that no English word has ("varunnu", "kittunnilla").
function isManglishVerb(w) {
  return w.length >= 5 && (w.endsWith('unnu') || w.endsWith('unnilla') || w.endsWith('unnund')
    || w.endsWith('nnathu') || w.endsWith('kkano'));
}

function latinLang(text) {
  const words = text.toLowerCase().match(/[a-z]+/g) || [];
  let ml = 0;
  let hi = 0;
  for (const w of words) {
    if (MANGLISH.has(w) || isManglishVerb(w)) ml++;
    if (HINGLISH.has(w)) hi++;
  }
  const best = Math.max(ml, hi);
  if (best === 0 || ml === hi) return null;
  if (!(best >= 2 || best * 3 >= words.length)) return null;
  return ml > hi ? 'ml' : 'hi';
}

/**
 * @returns {{ lang: 'ml'|'hi', replyLang: 'ml'|'hi' } | null}
 *   null for English — the message is sent exactly as typed, no translate call.
 */
export function chatLanguage(text) {
  if (!text || !text.trim()) return null;
  const script = textLang(text);
  if (script) return { lang: script, replyLang: script };
  const latin = latinLang(text);
  if (latin === 'ml') return { lang: 'ml', replyLang: MANGLISH_REPLY_LANG };
  if (latin === 'hi') return { lang: 'hi', replyLang: HINGLISH_REPLY_LANG };
  return null;
}
